import { computed, signal } from "@preact/signals-core";

const RETRY_BASE = 1000;
const RETRY_MAX = 30000;
// The timeout covers the body too, so it grows with each failure: a large
// scene on a slow link must still arrive whole.
const SNAPSHOT_TIMEOUT = 15000;
const SNAPSHOT_TIMEOUT_MAX = 120000;

export function createSync({
	state,
	outbox,
	subscribe,
	fetchSnapshot,
	accepts = (message) => String(message.scene_id) === String(state.confirmed.peek().scene?.id),
	window: win = globalThis.window,
	document: doc = globalThis.document,
	setTimeout = globalThis.setTimeout,
	clearTimeout = globalThis.clearTimeout,
	onMessage = (_message) => {},
	onGone = () => {},
	timeoutSignal = (ms) => AbortSignal.timeout(ms),
}) {
	const channel = signal("connecting");
	const online = signal(win?.navigator?.onLine ?? true);
	const syncing = signal(false);
	const denied = signal(false);
	const status = computed(() => {
		if (denied.value) return "rejected";
		if (!online.value || channel.value === "disconnected") return "offline";
		if (syncing.value || channel.value === "connecting") return "syncing";
		return "live";
	});

	let buffer = null;
	let again = false;
	let retryTimer = null;
	let retryDelay = RETRY_BASE;
	let snapshotTimeout = SNAPSHOT_TIMEOUT;
	let destroyed = false;
	let halted = false;

	function apply(diff) {
		const current = state.version.peek();
		if (diff.version <= current) {
			if (diff.mutation_id) state.resolvePending(diff.mutation_id);
			return "duplicate";
		}
		if (diff.version > current + 1) return "gap";
		state.applyDiff({ version: diff.version, ops: diff.ops, mutationId: diff.mutation_id });
		return "applied";
	}

	function replay(diffs) {
		let gap = false;
		for (const diff of diffs.sort((a, b) => a.version - b.version)) {
			if (apply(diff) === "gap") gap = true;
		}
		return gap;
	}

	// A call during a fetch runs one more afterwards: what it came to settle may
	// have been acknowledged after this fetch collected the settled changes.
	async function resync(initial = []) {
		if (destroyed || halted) return;
		if (buffer) {
			again = true;
			return;
		}
		buffer = [...initial];
		syncing.value = true;
		clearRetry();
		// Changes the server accepted before the fetch are inside the snapshot,
		// even when their echo was lost while disconnected.
		const settled = state.pending
			.peek()
			.filter((entry) => entry.acked)
			.map((entry) => entry.mutationId);

		let snapshot = null;
		let failure = null;
		try {
			snapshot = await fetchSnapshot({ signal: timeoutSignal(snapshotTimeout) });
		} catch (error) {
			failure = error;
		}
		if (destroyed) return;
		// A missing scene is gone for good, and a refused one will not come back
		// by retrying; neither is a network blip.
		if (failure?.status === 404) return halt(onGone);
		if (failure?.status === 401 || failure?.status === 403) return halt(deny);

		const buffered = buffer;
		buffer = null;
		snapshotTimeout = snapshot ? SNAPSHOT_TIMEOUT : Math.min(snapshotTimeout * 2, SNAPSHOT_TIMEOUT_MAX);
		if (snapshot) {
			online.value = true;
			if (snapshot.version >= state.version.peek()) state.replaceSnapshot(snapshot);
			for (const mutationId of settled) state.resolvePending(mutationId);
		}
		const gap = replay(buffered);
		syncing.value = false;
		if (snapshot && !gap) retryDelay = RETRY_BASE;
		else scheduleRetry();
		outbox.flush();
		if (again) {
			again = false;
			resync();
		}
	}

	function halt(then) {
		halted = true;
		buffer = null;
		again = false;
		syncing.value = false;
		clearRetry();
		then();
	}

	function deny() {
		halted = true;
		clearRetry();
		denied.value = true;
	}

	function scheduleRetry() {
		clearRetry();
		retryTimer = setTimeout(() => {
			retryTimer = null;
			resync();
		}, retryDelay);
		retryDelay = Math.min(retryDelay * 2, RETRY_MAX);
	}

	function clearRetry() {
		if (retryTimer) clearTimeout(retryTimer);
		retryTimer = null;
	}

	function received(message) {
		if (message?.type === "revoked") {
			deny();
		} else if (message?.type === "stale") {
			if (message.version > state.version.peek()) resync();
		} else if (message?.type !== "diff" || !accepts(message)) {
			onMessage(message);
		} else if (buffer) {
			buffer.push(message);
		} else if (apply(message) === "gap") {
			resync([message]);
		}
	}

	const subscription = subscribe({
		// The page's inline snapshot misses whatever changed before the
		// subscription existed, so the first connection resyncs too.
		connected() {
			channel.value = "connected";
			online.value = true;
			resync();
		},
		disconnected() {
			channel.value = "disconnected";
		},
		rejected: deny,
		received,
	});

	const onOnline = () => {
		online.value = true;
		resync();
	};
	const onOffline = () => {
		online.value = false;
	};
	const onVisibility = () => {
		if (doc.visibilityState === "visible") resync();
	};
	win?.addEventListener("online", onOnline);
	win?.addEventListener("offline", onOffline);
	doc?.addEventListener("visibilitychange", onVisibility);

	function destroy() {
		destroyed = true;
		clearRetry();
		win?.removeEventListener("online", onOnline);
		win?.removeEventListener("offline", onOffline);
		doc?.removeEventListener("visibilitychange", onVisibility);
		subscription?.unsubscribe?.();
	}

	return { status, resync, destroy };
}
